import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { ServisfyLogo } from '../Common/ServisfyLogo';
import { useLanguage } from '../../contexts/LanguageContext';

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 8;

const SignupSuccess = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const isTrial = params.get('trial') === '1';
  const [status, setStatus] = useState(isTrial ? 'success' : 'loading'); // loading | success | failed | timeout
  const [signup, setSignup] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('pending_signup');
    if (stored) {
      try { setSignup(JSON.parse(stored)); } catch (e) { /* noop */ }
    }
  }, []);

  useEffect(() => {
    if (isTrial) return; // No polling needed — trial is activated immediately
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout/status/${sessionId}`
        );
        if (!res.ok) throw new Error('Status check failed');
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (data.status === 'expired') {
          setStatus('failed');
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus('timeout');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus('timeout');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <Card className="w-full max-w-lg backdrop-blur-sm bg-white/90 border-0 shadow-xl">
        <CardHeader>
          <div className="flex justify-center mb-3"><ServisfyLogo variant="full" size="lg" /></div>
          <CardTitle className="text-center text-2xl">
            {status === 'loading' && t('success.confirming')}
            {status === 'success' && (isTrial ? t('success.trialActive') : t('success.welcome'))}
            {status === 'failed' && t('success.paymentIncomplete')}
            {status === 'timeout' && t('success.stillProcessing')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 text-center">
          {status === 'loading' && (
            <Loader2 data-testid="signup-success-spinner" className="h-12 w-12 mx-auto animate-spin text-purple-600" />
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="h-16 w-16 mx-auto text-green-500" data-testid="signup-success-icon" />
              <p className="text-gray-700">
                {isTrial
                  ? t('success.trialLive')
                  : t('success.subActive')}
              </p>
              {signup?.brand_id && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">{t('success.yourBrandId')}</p>
                  <p className="text-3xl font-bold text-green-700 tracking-widest" data-testid="brand-id-display">
                    {signup.brand_id}
                  </p>
                </div>
              )}
              {isTrial && signup?.trial_end_at && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md py-2 px-3" data-testid="trial-end-date">
                  {t('success.trialEnds')} {new Date(signup.trial_end_at).toLocaleDateString()}
                </div>
              )}
              {signup?.tier_id && (
                <Badge className="bg-purple-100 text-purple-800 capitalize">
                  {isTrial ? t('success.tierTrial', { tier: signup.tier_id }) : t('success.tierPlan', { tier: signup.tier_id })}
                </Badge>
              )}
              <Button
                onClick={() => { localStorage.removeItem('pending_signup'); navigate('/login'); }}
                className="w-full bg-gradient-to-r from-green-500 to-blue-600"
                data-testid="signup-success-continue-btn"
              >
                {t('success.continueLogin')}
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircle className="h-16 w-16 mx-auto text-red-500" />
              <p className="text-gray-700">{t('success.paymentFailed')}</p>
              <Button onClick={() => navigate('/signup')} className="w-full">{t('success.backToSignup')}</Button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-amber-600" />
              <p className="text-gray-700">
                {t('success.stillProcessingDesc')}
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">{t('signup.goToLogin')}</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupSuccess;